// Where the voice orb sits in the kadmon panel — 17 Sep 2026, Natanel: big in the middle of the panel,
// as ChatGPT's voice screen has it, but never hiding what kadmon puts on screen.
//
// THE RULE. The orb is big and centred over the chat while the call opens and until she first puts
// something on screen — people found, a card to confirm, a person looked up. From then on it sits at
// the top for the rest of the conversation, so the chat underneath can be read; a card waiting for a
// tap always puts it there, because the recruiter should read the card (a name, a number, which
// Search) before saying yes to it. A tap on the orb swaps the two, and the tap holds until what is
// on screen changes: a new card, or the first thing put there (`heldFlip`).
//
// WHY NOT BY WHETHER SHE IS SPEAKING (24 Sep 2026, Natanel: "it comes in and out sometimes when
// there's a chat on the page, it isn't smooth and consistent"). Until today the orb docked only
// while she was speaking about what she had shown, grew back whenever she listened or thought, and
// a tap let go whenever that state changed. A spoken question runs through listening, thinking,
// "one moment", thinking again and speaking — and on ElevenLabs "speaking" flickers between audio
// segments — so over a chat full of people the orb went up and down several times a question, and a
// tap to keep it somewhere was undone within a second. The layout now follows only what is on
// screen, which changes when an answer lands and at no other time.
//
// PURE. The panel (`useVoiceStage` in app/voice.tsx) and the orb's test page call the same functions.
import type { VoiceState } from './voice-mode.ts';

export type StageLayout = 'centre' | 'docked';

export interface StageFacts {
  state: VoiceState;
  /** A card the last answer left waiting for a tap. */
  card: string | null;
  /** She has put something on screen in this conversation: people, cards, a person looked up. */
  showing: boolean;
}

export function stageLayout(facts: StageFacts): StageLayout {
  return facts.card !== null || facts.showing ? 'docked' : 'centre';
}

/** A tap on the orb: the other layout, held while what is on screen stays as it was tapped over. */
export interface StageFlip {
  card: string | null;
  showing: boolean;
  layout: StageLayout;
}

/** The tap still in force: none once a new card, or the first thing on screen, has arrived. */
export function heldFlip(flip: StageFlip | null, facts: StageFacts): StageFlip | null {
  return flip !== null && flip.card === facts.card && flip.showing === facts.showing ? flip : null;
}

export function shownLayout(facts: StageFacts, flip: StageFlip | null): StageLayout {
  return heldFlip(flip, facts)?.layout ?? stageLayout(facts);
}
